import { useState, useRef, useCallback } from 'react';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

/**
 * Hook personalizado para paginación eficiente con CURSOR PAGINATION
 * Carga solo 8 productos por vez desde Firebase (no carga todos)
 */
export const usePagination = (itemsPerPage = 8) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  
  // Guardamos cursores para navegar sin depender de índices complejos
  const cursorsRef = useRef({});
  const currentPageRef = useRef(1);
  const pageHistoryRef = useRef([]); // Historial: [null, cursor1, cursor2, cursor3...]
  const currentSearchTermRef = useRef(''); // Guardar término de búsqueda actual

  /**
   * Construye una query de Firestore con filtros opcionales
   * Usa __name__ (ID del documento) para cursor pagination sin necesidad de índices complejos
   */

  /**
   * Cargar primera página con filtros - SOLO TRAE 9 PRODUCTOS (8 + 1 para verificar siguiente)
   * Si hay búsqueda, consulta Firestore con prefijo en nombre (búsqueda en BD, no en cliente)
   */
  const loadFirstPage = useCallback(async (constraints = {}) => {
    setIsLoading(true);
    try {
      const { category = '', brands = [], searchTerm = '', enabled = true } = constraints;
      
      // Resetear paginación
      pageHistoryRef.current = [null];
      currentPageRef.current = 1;
      currentSearchTermRef.current = searchTerm;
      
      let allDocsFromFirebase = [];
      
      // 🔍 Si hay búsqueda, hacer DOS queries (name + description) y combinar
      if (searchTerm) {
        const searchUpper = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();
        
        console.log('🔍 Buscando:', searchUpper);
        
        // Query 1: Buscar por prefijo en NAME (probando con mayúscula inicial primero)
        let nameQuery = query(
          collection(db, 'products'),
          where('enabled', '==', enabled),
          ...(category ? [where('category', '==', category)] : []),
          ...(brands.length > 0 ? [where('name', 'in', brands.slice(0, 10))] : []),
          where('name', '>=', searchUpper),
          where('name', '<', searchUpper.slice(0, -1) + String.fromCharCode(searchUpper.charCodeAt(searchUpper.length - 1) + 1)),
          orderBy('name'),
          limit(itemsPerPage + 1)
        );
        
        // Query 2: Buscar por prefijo en DESCRIPTION (solo si no hay filtro de marca)
        let descQuery = null;
        if (brands.length === 0) {
          descQuery = query(
            collection(db, 'products'),
            where('enabled', '==', enabled),
            ...(category ? [where('category', '==', category)] : []),
            where('description', '>=', searchUpper),
            where('description', '<', searchUpper.slice(0, -1) + String.fromCharCode(searchUpper.charCodeAt(searchUpper.length - 1) + 1)),
            orderBy('description'),
            limit(itemsPerPage + 1)
          );
        }
        
        try {
          const nameSnapshot = await getDocs(nameQuery);
          const nameResults = nameSnapshot.docs;
          console.log(`📄 NAME: ${nameResults.length} resultados`);
          
          let descResults = [];
          if (descQuery) {
            const descSnapshot = await getDocs(descQuery);
            descResults = descSnapshot.docs;
            console.log(`📋 DESCRIPTION: ${descResults.length} resultados`);
          }
          
          // Combinar resultados de ambas queries (evitar duplicados por ID)
          const docsMap = new Map();
          nameResults.forEach(doc => docsMap.set(doc.id, doc));
          descResults.forEach(doc => docsMap.set(doc.id, doc));
          
          allDocsFromFirebase = Array.from(docsMap.values()).slice(0, itemsPerPage + 1);
          console.log(`✅ Total: ${allDocsFromFirebase.length}`);
        } catch (error) {
          console.error('❌ Error en búsqueda dual:', error.message, error.code);
          // Fallback: traer todos y filtrar en cliente
          console.log('📌 Haciendo fallback: traer todos y filtrar en cliente...');
          try {
            const allProductsQuery = query(
              collection(db, 'products'),
              where('enabled', '==', enabled),
              ...(category ? [where('category', '==', category)] : []),
              ...(brands.length > 0 ? [where('name', 'in', brands.slice(0, 10))] : []),
              limit(100) // Traer hasta 100 para buscar
            );
            const snapshot = await getDocs(allProductsQuery);
            allDocsFromFirebase = snapshot.docs;
            console.log('📌 Fallback: traídos', allDocsFromFirebase.length, 'documentos para filtrar en cliente');
          } catch (fallbackError) {
            console.error('❌ Fallback también falló:', fallbackError.message);
          }
        }
      } else {
        // Sin búsqueda, usar query normal con cursor pagination
        let whereConditions = [where('enabled', '==', enabled)];
        
        if (category) {
          whereConditions.push(where('category', '==', category));
        }
        
        if (brands.length > 0) {
          whereConditions.push(where('name', 'in', brands.slice(0, 10)));
        }
        
        const baseQuery = query(
          collection(db, 'products'),
          ...whereConditions,
          orderBy('__name__'),
          limit(itemsPerPage + 1)
        );
        
        const snapshot = await getDocs(baseQuery);
        allDocsFromFirebase = snapshot.docs;
      }
      
      // Mapear documentos de Firestore
      const allDocs = allDocsFromFirebase.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtro final en cliente (garantiza que coincida, case-insensitive)
      let filtered = allDocs;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = allDocs.filter(product =>
          (product.name && product.name.toLowerCase().includes(searchLower)) ||
          (product.description && product.description.toLowerCase().includes(searchLower))
        );
        console.log(`✅ Cliente: ${filtered.length}/${allDocs.length}`);
      }

      const hasMore = allDocsFromFirebase.length > itemsPerPage;
      
      // Guardar cursor para siguiente página (MUST ser documento original de Firestore)
      if (allDocsFromFirebase.length > itemsPerPage) {
        const lastDocOfPage = allDocsFromFirebase[itemsPerPage - 1];
        pageHistoryRef.current[2] = lastDocOfPage;
      }

      // Tomar solo los items de esta página
      const pageItems = filtered.slice(0, itemsPerPage);
      
      setItems(pageItems);
      setHasNextPage(hasMore);
      setHasPrevPage(false);
      
      return {
        items: pageItems,
        hasNext: hasMore,
        hasPrev: false
      };
    } catch (error) {
      console.error('❌ Error en loadFirstPage:', error);
      setItems([]);
      setHasNextPage(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [itemsPerPage]);

  /**
   * Cargar página siguiente - SOLO TRAE 9 PRODUCTOS (8 + 1)
   * Si hay búsqueda, hace dos queries (name + description)
   */
  const loadNextPage = useCallback(async (constraints = {}) => {
    if (!hasNextPage) return null;
    
    setIsLoading(true);
    try {
      const { category = '', brands = [], searchTerm = '', enabled = true } = constraints;
      
      const currentPage = currentPageRef.current;
      const startCursor = pageHistoryRef.current[currentPage + 1];
      
      if (!startCursor) {
        console.warn('⚠️ No hay cursor para página', currentPage + 1);
        return null;
      }

      let allDocsFromFirebase = [];
      
      // 🔍 Si hay búsqueda, hacer DOS queries y combinar
      if (searchTerm) {
        const searchUpper = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();
        
        const nameQuery = query(
          collection(db, 'products'),
          where('enabled', '==', enabled),
          ...(category ? [where('category', '==', category)] : []),
          ...(brands.length > 0 ? [where('name', 'in', brands.slice(0, 10))] : []),
          where('name', '>=', searchUpper),
          where('name', '<', searchUpper.slice(0, -1) + String.fromCharCode(searchUpper.charCodeAt(searchUpper.length - 1) + 1)),
          orderBy('name'),
          startAfter(startCursor),
          limit(itemsPerPage + 1)
        );
        
        let descQuery = null;
        if (brands.length === 0) {
          descQuery = query(
            collection(db, 'products'),
            where('enabled', '==', enabled),
            ...(category ? [where('category', '==', category)] : []),
            where('description', '>=', searchUpper),
            where('description', '<', searchUpper.slice(0, -1) + String.fromCharCode(searchUpper.charCodeAt(searchUpper.length - 1) + 1)),
            orderBy('description'),
            startAfter(startCursor),
            limit(itemsPerPage + 1)
          );
        }
        
        try {
          const nameSnapshot = await getDocs(nameQuery);
          const nameResults = nameSnapshot.docs;
          console.log('🔍 loadNextPage NAME:', nameResults.length);
          
          let descResults = [];
          if (descQuery) {
            const descSnapshot = await getDocs(descQuery);
            descResults = descSnapshot.docs;
            console.log('🔍 loadNextPage DESCRIPTION:', descResults.length);
          }
          
          const docsMap = new Map();
          nameResults.forEach(doc => docsMap.set(doc.id, doc));
          descResults.forEach(doc => docsMap.set(doc.id, doc));
          
          allDocsFromFirebase = Array.from(docsMap.values()).slice(0, itemsPerPage + 1);
          console.log(`📊 loadNextPage - Total: ${allDocsFromFirebase.length}`);
        } catch (error) {
          console.error('❌ loadNextPage error:', error.message);
          try {
            const allProductsQuery = query(
              collection(db, 'products'),
              where('enabled', '==', enabled),
              ...(category ? [where('category', '==', category)] : []),
              ...(brands.length > 0 ? [where('name', 'in', brands.slice(0, 10))] : []),
              startAfter(startCursor),
              limit(itemsPerPage + 1)
            );
            const snapshot = await getDocs(allProductsQuery);
            allDocsFromFirebase = snapshot.docs;
            console.log('📌 loadNextPage Fallback:', allDocsFromFirebase.length);
          } catch (fallbackError) {
            console.error('❌ loadNextPage Fallback failed:', fallbackError.message);
          }
        }
      } else {
        // Sin búsqueda, query normal
        let whereConditions = [where('enabled', '==', enabled)];
        
        if (category) {
          whereConditions.push(where('category', '==', category));
        }
        
        if (brands.length > 0) {
          whereConditions.push(where('name', 'in', brands.slice(0, 10)));
        }

        const nextQuery = query(
          collection(db, 'products'),
          ...whereConditions,
          orderBy('__name__'),
          startAfter(startCursor),
          limit(itemsPerPage + 1)
        );
        
        const snapshot = await getDocs(nextQuery);
        allDocsFromFirebase = snapshot.docs;
      }
      
      const allDocs = allDocsFromFirebase.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrar por descripción también si hay búsqueda (case-insensitive)
      let filtered = allDocs;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = allDocs.filter(product =>
          (product.name && product.name.toLowerCase().includes(searchLower)) ||
          (product.description && product.description.toLowerCase().includes(searchLower))
        );
        console.log(`✅ loadNextPage cliente: ${filtered.length}/${allDocs.length}`);
      }
      
      const nextHasMore = allDocsFromFirebase.length > itemsPerPage;
      
      // Guardar cursor para próxima página (MUST ser documento Firestore original)
      if (allDocsFromFirebase.length > itemsPerPage) {
        const lastDocOfPage = allDocsFromFirebase[itemsPerPage - 1];
        pageHistoryRef.current[currentPage + 2] = lastDocOfPage;
      }

      const pageItems = filtered.slice(0, itemsPerPage);
      
      setItems(pageItems);
      setHasNextPage(nextHasMore);
      setHasPrevPage(true);
      currentPageRef.current = currentPage + 1;

      return {
        items: pageItems,
        hasNext: nextHasMore,
        hasPrev: true
      };
    } catch (error) {
      console.error('❌ Error en loadNextPage:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [hasNextPage, itemsPerPage]);

  /**
   * Cargar página anterior
   * Para búsqueda dual, recarga desde el inicio y pagina en cliente (más seguro)
   */
  const loadPrevPage = useCallback(async (constraints = {}) => {
    if (!hasPrevPage || currentPageRef.current <= 1) return null;
    
    setIsLoading(true);
    try {
      const { category = '', brands = [], searchTerm = '', enabled = true } = constraints;
      const prevPageNum = currentPageRef.current - 1;
      
      // Para ir atrás con múltiples queries, es más seguro recargar desde el inicio
      // Esto garantiza consistencia en los resultados
      let allDocsFromFirebase = [];
      
      if (searchTerm) {
        // Búsqueda dual: name + description (SIN cursor, desde el principio)
        const searchUpper = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();
        
        const nameQuery = query(
          collection(db, 'products'),
          where('enabled', '==', enabled),
          ...(category ? [where('category', '==', category)] : []),
          ...(brands.length > 0 ? [where('name', 'in', brands.slice(0, 10))] : []),
          where('name', '>=', searchUpper),
          where('name', '<', searchUpper.slice(0, -1) + String.fromCharCode(searchUpper.charCodeAt(searchUpper.length - 1) + 1)),
          orderBy('name'),
          limit((prevPageNum * itemsPerPage) + itemsPerPage + 1) // Traer hasta esta página + 1
        );
        
        let descQuery = null;
        if (brands.length === 0) {
          descQuery = query(
            collection(db, 'products'),
            where('enabled', '==', enabled),
            ...(category ? [where('category', '==', category)] : []),
            where('description', '>=', searchUpper),
            where('description', '<', searchUpper.slice(0, -1) + String.fromCharCode(searchUpper.charCodeAt(searchUpper.length - 1) + 1)),
            orderBy('description'),
            limit((prevPageNum * itemsPerPage) + itemsPerPage + 1)
          );
        }
        
        try {
          const nameSnapshot = await getDocs(nameQuery);
          const nameResults = nameSnapshot.docs;
          
          let descResults = [];
          if (descQuery) {
            const descSnapshot = await getDocs(descQuery);
            descResults = descSnapshot.docs;
          }
          
          const docsMap = new Map();
          nameResults.forEach(doc => docsMap.set(doc.id, doc));
          descResults.forEach(doc => docsMap.set(doc.id, doc));
          
          allDocsFromFirebase = Array.from(docsMap.values());
          console.log(`🔍 loadPrevPage - NAME: ${nameResults.length}, DESC: ${descResults.length} → Total: ${allDocsFromFirebase.length}`);
        } catch (error) {
          console.error('❌ loadPrevPage error:', error.message);
          try {
            const snapshot = await getDocs(nameQuery);
            allDocsFromFirebase = snapshot.docs;
            console.log('📌 loadPrevPage Fallback:', allDocsFromFirebase.length);
          } catch (fallbackError) {
            console.error('❌ loadPrevPage Fallback failed:', fallbackError.message);
          }
        }
      } else {
        // Sin búsqueda, usar cursor de página anterior
        const startCursor = pageHistoryRef.current[prevPageNum] || null;

        let whereConditions = [where('enabled', '==', enabled)];
        
        if (category) {
          whereConditions.push(where('category', '==', category));
        }
        
        if (brands.length > 0) {
          whereConditions.push(where('name', 'in', brands.slice(0, 10)));
        }
        
        let prevQuery;
        if (startCursor) {
          prevQuery = query(
            collection(db, 'products'),
            ...whereConditions,
            orderBy('__name__'),
            startAfter(startCursor),
            limit(itemsPerPage + 1)
          );
        } else {
          prevQuery = query(
            collection(db, 'products'),
            ...whereConditions,
            orderBy('__name__'),
            limit(itemsPerPage + 1)
          );
        }
        
        const snapshot = await getDocs(prevQuery);
        allDocsFromFirebase = snapshot.docs;
      }
      
      const allDocs = allDocsFromFirebase.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filtro final en cliente (case-insensitive)
      let filtered = allDocs;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = allDocs.filter(product =>
          (product.name && product.name.toLowerCase().includes(searchLower)) ||
          (product.description && product.description.toLowerCase().includes(searchLower))
        );
        console.log(`✅ loadPrevPage cliente: ${filtered.length}/${allDocs.length}`);
      }
      
      // Para búsqueda, paginar en cliente después de filtrar
      let pageItems = [];
      if (searchTerm) {
        const start = (prevPageNum - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        pageItems = filtered.slice(start, end);
      } else {
        pageItems = filtered.slice(0, itemsPerPage);
      }
      
      setItems(pageItems);
      setHasPrevPage(prevPageNum > 1);
      setHasNextPage(true); // Siempre hay siguiente si volvemos atrás
      currentPageRef.current = prevPageNum;

      return {
        items: pageItems,
        hasNext: true,
        hasPrev: prevPageNum > 1
      };
    } catch (error) {
      console.error('❌ Error en loadPrevPage:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [hasPrevPage, itemsPerPage]);

  /**
   * Resetear paginación
   */
  const reset = useCallback(() => {
    setItems([]);
    setIsLoading(false);
    setHasNextPage(false);
    setHasPrevPage(false);
    cursorsRef.current = {};
    pageHistoryRef.current = [null];
    currentPageRef.current = 1;
  }, []);

  return {
    items,
    isLoading,
    hasNextPage,
    hasPrevPage,
    loadFirstPage,
    loadNextPage,
    loadPrevPage,
    reset,
    currentPage: currentPageRef.current
  };
};

export default usePagination;
